<template>
  <wizard-container
    class="check-availability"
    :class="{ 'availability-check-main': activeWizard }"
  >
    <p v-if="!activeWizard" class="cs-name">
      {{ campsiteName }}
    </p>
    <dates-wizard
      :open="activeWizard === 'dates'"
      :dates="dates"
      :availability="datepickerAvailability"
      :categories="selectedCategories"
      :show-summary="!activeWizard"
      :opening-dates="openingDates"
      :required="true"
      :highlight-errors="highlightErrors"
      @open="openWizard('dates')"
      @cancel="closeWizard()"
      @change-dates="selectDates($event)"
    />
    <guests-wizard
      :open="activeWizard === 'guests'"
      :show-summary="!activeWizard"
      :guests="guests"
      @open="openWizard('guests')"
      @cancel="closeWizard()"
      @change-guests="selectGuests($event)"
    />
    <accommodation-wizard
      :open="activeWizard === 'accommodation'"
      :show-summary="!activeWizard"
      :category-ids="categoryIds"
      :allowed-category-ids="allowedCategoryIds"
      :search-filters="searchFiltersWithFacets"
      :show-counts="false"
      @open="openWizard('accommodation')"
      @close="closeWizard()"
      @cancel="closeWizard()"
      @change-category-ids="selectCategoryIds($event)"
      @change-facets="selectFacets($event)"
    />

    <div v-if="!activeWizard">
      <span @click="submitClicked()">
        <pu-campsite-link
          data-test="availability-link"
          v-bind="campsiteLinkProps"
          class="primary asblock button"
        >
          <translate>Check availability</translate>
        </pu-campsite-link>
      </span>
      <translate
        v-if="campsite.adultsOnly"
        :class="[
          {
            'adults-only-with-child-ages': campsite.adultsOnly && hasChildren,
          },
          'ages-help-text',
        ]"
        :tt="{ minAdultAge }"
      >
        This listing accepts adults only (defined as age {minAdultAge}+)
      </translate>
      <translate
        v-else-if="hasChildren"
        class="ages-help-text center"
        :tt="{ minAdultAge }"
      >
        (listing defines adults as age {minAdultAge}+)
      </translate>
      <wizard-footer @clear="clear()">
        <template #cancel>
          <pu-campsite-link v-bind="backLinkProps" class="minimal sml button">
            <translate>Cancel</translate>
          </pu-campsite-link>
        </template>
      </wizard-footer>
    </div>
    <nuxt-to-meta :nuxt2meta="head" />
  </wizard-container>
</template>
<script lang="ts">
import { ref } from 'vue'
import { getCampsiteCategoriesPresenter } from '~/apps/categories/presenter-json/CampsiteCategoriesUpdaterJson'
import { DatepickerAvailabilityBaseComponent } from '~/apps/datepicker-availability/DatepickerAvailabilityBaseComponent'
import { addDays } from '~/utility/date/relative'
import {
  Component,
  Prop,
  Setup,
  toNative,
  Watch,
} from '~/utility/pu-class-decorator'
import type {
  CampsiteDetails,
  PitchType,
} from '../../../campsite-details/types'
import { routeToFilters } from '../../../pu-links/infrastructure/routeToFilters'
import { PuCampsiteLinkProps } from '../../../pu-links/link-campsite/PuCampsiteLinkProps'
import { defaultSearchParty } from '../../../search/defaults'
import { SearchParty } from '../../../search/schemas'
import { ArriveDepartIso } from '../../../search/types'
import { SearchFiltersUrl } from '../../../url/search-filters/SearchFiltersUrl'
import { validateAvailabilityCheck } from '../../availability/validation'
import { OpeningDatesRange } from '../../types'

type WizardName = 'accommodation' | 'dates' | 'guests'

@Component({
  setup: () => {
    const route = useRoute()
    const filters = routeToFilters(route)
    const highlightErrors = ref(false)
    const dates = ref(filters.dates || null)
    const categoryIds = ref(filters.categoryIds || [])
    const guests = ref(filters.party || defaultSearchParty)
    const facets = ref(filters.facets || [])

    const submitClicked = () => {
      highlightErrors.value = true
    }
    return {
      highlightErrors,
      categoryIds,
      guests,
      dates,
      facets,
      submitClicked,
    }
  },
})
class AvailabilityCheckWizardMain extends DatepickerAvailabilityBaseComponent {
  @Prop({ required: true, type: Object })
  campsite: CampsiteDetails

  @Prop({ required: false, type: Object })
  pitchtype?: PitchType

  get campsiteName() {
    return this.campsite.name
  }

  get datepickerAvailabilityCampsiteSlug() {
    return this.campsite.slug
  }

  get datepickerAvailabilitySelectedCategoryIds() {
    return this.categoryIds
  }

  get allowedCategoryIds(): string[] {
    return this.campsite.pitchCategoryIds
      ? this.campsite.pitchCategoryIds.map((cat) => `${cat}`)
      : this.campsite.categories.map((cat) => `${cat.id}`)
  }

  get minAdultAge(): number {
    return this.campsite.minAdultAge
  }

  get hasChildren() {
    return this.guests.childAges.length > 0
  }

  activeWizard: WizardName | '' = ''
  openWizard(name: WizardName) {
    this.activeWizard = name
  }

  closeWizard() {
    this.activeWizard = ''
  }

  categoryIds: string[] = []
  guests: SearchParty = defaultSearchParty
  dates: ArriveDepartIso | null = null
  facets: string[] = []

  @Setup(() => {
    const route = useRoute()
    const presenter = getCampsiteCategoriesPresenter(
      (route.params.lang as string) || 'en-gb',
    )

    return presenter.byIdMap
  })
  categories: ReturnType<typeof getCampsiteCategoriesPresenter>['byIdMap']

  async mounted() {
    await this.initAvailability(this.$route.params.lang || 'en-gb')
  }

  get head() {
    return {
      title: `${this.$t('Edit search')} | ${this.campsite.name}, ${
        this.campsite.hierarchyTextShort
      } | Pitchup.com`,
      bodyAttrs: {
        class: 'noscroll',
      },
    }
  }

  get openingDates(): OpeningDatesRange[] {
    const openingDateRanges = this.campsite.openingDateRanges
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- TODO: Type openingDateRanges properly
    return openingDateRanges ? this.mapOpeningDateRanges(openingDateRanges) : []
  }

  /* eslint-disable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- TODO: Type openingDateRanges properly */
  private mapOpeningDateRanges(openingDateRanges) {
    return openingDateRanges.map((r) => {
      return {
        from: r.openingDate,
        to: r.closingDate,
      }
    })
  }
  /* eslint-enable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */

  @Watch('categoryIds', { deep: true })
  reloadAvailability() {
    this.updateAvailability()
  }

  selectCategoryIds(categoryIds: string[]) {
    this.categoryIds = categoryIds
  }

  selectFacets(facets: string[]) {
    this.facets = [...facets]
  }

  get searchFiltersWithFacets() {
    return {
      ...routeToFilters(this.$route),
      facets: this.facets,
    }
  }

  get selectedCategories() {
    return this.categoryIds.map((id) => this.categories[id])
  }

  selectGuests(guests: SearchParty) {
    this.guests = guests
    this.closeWizard()
  }

  selectDates(dates: ArriveDepartIso | null) {
    this.dates = dates
      ? {
          arrive: dates.arrive,
          depart: dates.depart ? dates.depart : addDays(1, dates.arrive),
        }
      : null
    this.closeWizard()
  }

  get isSearchEnabled() {
    return !validateAvailabilityCheck({ dates: this.dates })
  }

  get updatedSearchFilters() {
    return {
      categoryIds: this.categoryIds,
      party: this.guests,
      dates: this.dates || undefined,
      facets: this.facets.length > 0 ? this.facets : undefined,
    }
  }

  clear() {
    this.guests = defaultSearchParty
    this.dates = null
    this.categoryIds = []
    this.facets = []
  }

  get searchFilters() {
    return routeToFilters(this.$route)
  }

  get campsiteLinkProps(): PuCampsiteLinkProps {
    return {
      disabled: !this.isSearchEnabled,
      hierarchyPath: this.campsite.path,
      slug: this.campsite.slug,
      pitchtypeId: this.pitchtype?.id || undefined,
      filters: this.updatedSearchFilters,
    }
  }

  get backLinkProps() {
    const referrer = this.$routerClient.getReferrer()
    const filters = referrer
      ? SearchFiltersUrl.urlToFilters(referrer?.query)
      : undefined
    return {
      disabled: false,
      filters,
      hierarchyPath: this.campsite.path,
      pitchtypeId: this.pitchtype?.id || undefined,
      prependUrlSearchFilters: false,
      slug: this.campsite.slug,
    }
  }
}

export default toNative(AvailabilityCheckWizardMain)
</script>
<style scoped>
p {
  font-weight: bold;
  color: var(--colour-mid-green);
  text-align: center;
  margin: 0 0 1em;
  overflow-wrap: break-word;
}

.check-availability.wizard-container {
  background: #999;
  width: 100vw;
  height: 100%;
  z-index: auto;
  position: absolute;
  top: 0;
}

.availability-check-main :deep(.search-form) {
  display: none;
}

.check-availability .accommodation-wizard {
  margin-bottom: 2em;
}

.ages-help-text {
  margin: 1em 0 0.5em;
  text-align: left;
  line-height: 1.2;
  display: block;
  font-size: var(--body);
  color: var(--colour-text-light);
}

.ages-help-text.center {
  text-align: center;
}

.adults-only-with-child-ages {
  font-weight: bold;
  color: var(--colour-text-default);
}
</style>
